// Estado global do Command Palette (Cmd+K / Ctrl+K).
// Mantido em Context API pra evitar adicionar zustand só pra isso.
// Expõe { open, setOpen, toggle } + hook useCommandPaletteShortcut() que
// registra o listener global de teclado uma única vez no Layout.

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";

type Ctx = {
  open: boolean;
  setOpen: (v: boolean) => void;
  toggle: () => void;
};

const CommandPaletteCtx = createContext<Ctx | null>(null);

export function CommandPaletteProvider({ children }: { children: ReactNode }) {
  const [open, setOpen] = useState(false);
  const toggle = useCallback(() => setOpen((v) => !v), []);
  const value = useMemo(() => ({ open, setOpen, toggle }), [open, toggle]);
  return (
    <CommandPaletteCtx.Provider value={value}>
      {children}
    </CommandPaletteCtx.Provider>
  );
}

export function useCommandPalette(): Ctx {
  const ctx = useContext(CommandPaletteCtx);
  if (!ctx) {
    // Fallback no-op para componentes que renderizam fora do provider
    // (ex.: tela de login). Evita crash duro.
    return { open: false, setOpen: () => {}, toggle: () => {} };
  }
  return ctx;
}

export function useCommandPaletteShortcut() {
  const { toggle } = useCommandPalette();
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const isMac = navigator.platform.toUpperCase().includes("MAC");
      const mod = isMac ? e.metaKey : e.ctrlKey;
      if (mod && (e.key === "k" || e.key === "K")) {
        e.preventDefault();
        toggle();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [toggle]);
}
